import * as exifr from "exifr";
import { exactHash, inspectImage } from "./metrics";
import type { PhotoRecord, RootFolder, ScanProgress } from "./types";

const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "webp", "bmp", "gif", "tif", "tiff", "avif", "heic", "heif"]);

type RawPhoto = Omit<PhotoRecord, "width" | "height" | "sharpness">;

async function walk(
  root: RootFolder,
  directory: FileSystemDirectoryHandle,
  prefix: string,
  output: RawPhoto[],
): Promise<void> {
  for await (const handle of directory.values()) {
    const relativePath = prefix ? `${prefix}/${handle.name}` : handle.name;
    if (handle.kind === "directory") {
      await walk(root, handle, relativePath, output);
      continue;
    }
    const extension = handle.name.split(".").pop()?.toLowerCase() ?? "";
    if (!IMAGE_EXTENSIONS.has(extension)) continue;
    const file = await handle.getFile();
    output.push({
      id: `${root.id}:${relativePath}`,
      rootId: root.id,
      rootName: root.name,
      name: handle.name,
      relativePath,
      handle,
      parentHandle: directory,
      file,
    });
  }
}

async function mapConcurrent<T, R>(
  values: T[],
  concurrency: number,
  callback: (value: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(values.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(concurrency, values.length) }, async () => {
    while (cursor < values.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await callback(values[index], index);
    }
  });
  await Promise.all(workers);
  return results;
}

function dateText(value: unknown): string | undefined {
  if (value instanceof Date && !Number.isNaN(value.valueOf())) return value.toISOString();
  if (typeof value === "string" && value.trim()) return value;
  return undefined;
}

export async function scanFolders(
  roots: RootFolder[],
  onProgress: (progress: ScanProgress) => void,
): Promise<PhotoRecord[]> {
  const raw: RawPhoto[] = [];
  for (let index = 0; index < roots.length; index += 1) {
    onProgress({ phase: "Discovering", current: index, total: roots.length, detail: roots[index].name });
    await walk(roots[index], roots[index].handle, "", raw);
  }

  onProgress({ phase: "Reading images", current: 0, total: raw.length, detail: "Preparing local analysis" });
  const records = await mapConcurrent(raw, Math.max(2, Math.min(6, navigator.hardwareConcurrency || 4)), async (item, index) => {
    let metadata: Record<string, unknown> = {};
    try {
      metadata = (await exifr.parse(item.file, {
        pick: ["DateTimeOriginal", "CreateDate", "ModifyDate", "Make", "Model", "Software"],
        translateValues: false,
      })) ?? {};
    } catch {
      metadata = {};
    }

    try {
      const inspected = await inspectImage(item.file);
      if (index % 4 === 0 || index === raw.length - 1) {
        onProgress({ phase: "Reading images", current: index + 1, total: raw.length, detail: item.relativePath });
      }
      return {
        ...item,
        ...inspected,
        takenAt: dateText(metadata.DateTimeOriginal ?? metadata.CreateDate ?? metadata.ModifyDate),
        camera: [metadata.Make, metadata.Model].filter(Boolean).join(" ") || undefined,
        software: typeof metadata.Software === "string" ? metadata.Software : undefined,
      } satisfies PhotoRecord;
    } catch (error) {
      return {
        ...item,
        width: 0,
        height: 0,
        sharpness: 0,
        decodeError: error instanceof Error ? error.message : "Unable to decode image",
      } satisfies PhotoRecord;
    }
  });

  const sizeBuckets = new Map<number, PhotoRecord[]>();
  for (const record of records) {
    const bucket = sizeBuckets.get(record.file.size) ?? [];
    bucket.push(record);
    sizeBuckets.set(record.file.size, bucket);
  }
  const exactCandidates = [...sizeBuckets.values()].filter((bucket) => bucket.length > 1).flat();
  await mapConcurrent(exactCandidates, 3, async (record, index) => {
    record.exactHash = await exactHash(record.file);
    if (index % 4 === 0 || index === exactCandidates.length - 1) {
      onProgress({ phase: "Exact matching", current: index + 1, total: exactCandidates.length, detail: record.relativePath });
    }
    return record;
  });

  return records;
}
